import { z } from 'zod';

/**
 * Staff in-app notifications (ADR-0014): a broadcast feed for panel staff
 * with per-user read state. Modules emit via `ctx.notify(…)`; the web
 * shell renders a bell with the unread count.
 */

export const NOTIFICATION_SEVERITIES = ['info', 'warning', 'critical'] as const;
export type NotificationSeverity = (typeof NOTIFICATION_SEVERITIES)[number];

export interface NotificationDto {
  id: string;
  /** Machine event type, e.g. `quota.disk.exceeded`. */
  type: string;
  severity: NotificationSeverity;
  title: string;
  body: string | null;
  /** Optional link target (e.g. subscription page). */
  entityType: string | null;
  entityId: string | null;
  createdAt: string;
  /** When the requesting user read it; null = unread for them. */
  readAt: string | null;
}

export interface NotificationListDto {
  items: NotificationDto[];
  total: number;
  /** Unread count for the requesting user (independent of pagination). */
  unreadCount: number;
}

export const notificationListQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  perPage: z.coerce.number().int().min(1).max(100).default(25),
  /** Only unread-for-me items. Query strings carry booleans as text. */
  unread: z
    .enum(['true', 'false'])
    .optional()
    .transform((v) => v === 'true'),
});
export type NotificationListQuery = z.infer<typeof notificationListQuerySchema>;
